import { useMemo } from 'react'
import { Box, Button, Fab, IconButton, Typography } from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined'
import { v4 as uuidv4 } from 'uuid'
import { useTranslation } from 'react-i18next'
import { DataGridPro } from '@mui/x-data-grid-pro'
import RiskMatrixEvaluatedData from './RiskMatrixEvaluatedData'
import RiskMatrixValueData from './RiskMatrixValueData'
import CustomGridTreeDataGroupingCell from '../../../components/common/CustomGridTreeDataGroupingCell'
import styles from './RiskMatrixResultModal.module.scss'
import RiskMatrixResultTitle from './RiskMatrixResultTitle'
import {
  useLazyGetLastRiskHistoryQuery,
  useLazyGetRiskHistoryQuery,
} from '../../../features/riskMatrix/riskMatrixSlice'

const RiskMatrixResultModal = ({
  riskHistory,
  country = 'BR',
  handleFetchHistory,
  handleFetchLastHistory,
  setModalOpen,
}) => {
  const { t } = useTranslation(['common', 'messages'])
  const [_, { isLoading: loadingGetRiskHistory }] = useLazyGetRiskHistoryQuery({ fixedCacheKey: 'get-risk-history' })
  const [__, { isLoading: loadingGetLastRiskHistory }] = useLazyGetLastRiskHistoryQuery({
    fixedCacheKey: 'get-last-risk-history',
  })

  const riskResult = useMemo(() => riskHistory?.current?.riskResult, [riskHistory])
  const riskScore = useMemo(() => riskHistory?.current?.riskScore, [riskHistory])

  const categoryRows = useMemo(() => {
    const categories = riskResult.results.reduce((acc, item) => {
      const categoryKey = item.category?.trim() || 'noCategory'
      acc[categoryKey] = acc[categoryKey] || { score: 0, isHit: false, category: categoryKey }

      if (item.isHit && item.score > acc[categoryKey].score) {
        acc[categoryKey] = item
      }

      return acc
    }, {})

    return Object.entries(categories).map(([categoryKey, item]) => ({
      ...item,
      id: uuidv4(),
      isGroup: true,
      groupedBy: categoryKey,
    }))
  }, [riskResult?.results])

  const [ruleRows, categoryErrors, combinatorErrors] = useMemo(() => {
    const categoriesWithError = new Set()
    const combinatorsWithError = new Set()
    const childrenRules = []
    const combinatorRows = []
    const rules = riskResult?.results
      .map((result) => {
        const id = result.id || uuidv4()
        const categoryKey = result.category?.trim() || 'noCategory'

        if (Array.isArray(result.groupResults)) {
          // Check if any child has errors
          const hasChildErrors = result.groupResults.some(
            (childRule) => childRule.dataToEvaluate === null || !childRule.operatorFound
          )

          // Add combinator row
          const combinatorId = `${categoryKey}/${result.combinator}`
          combinatorRows.push({
            id: uuidv4(),
            combinator: result.combinator,
            groupedBy: combinatorId,
            isGroup: true,
          })

          if (hasChildErrors) {
            combinatorsWithError.add(combinatorId)
          }

          result.groupResults.forEach((childRule) => {
            const childRuleId = childRule.id || uuidv4()
            childrenRules.push({
              ...childRule,
              id: childRuleId,
              groupedBy: `${categoryKey}/${result.combinator}/${childRuleId}`,
            })
          })
        }

        const groupedBy = `${categoryKey}/${id}`

        if (result.dataToEvaluate === null) {
          categoriesWithError.add(categoryKey)
        }

        return {
          ...result,
          id,
          groupedBy,
        }
      })
      .filter((result) => !result.combinator)

    return [[...rules, ...combinatorRows, ...childrenRules], [...categoriesWithError], combinatorsWithError]
  }, [riskResult?.results])

  const columns = [
    {
      field: 'dataToEvaluate',
      headerName: t('messages:riskmatrix.title.valueFound'),
      width: 250,
      renderCell: ({ row, rowNode, value }) => {
        if (rowNode.type === 'group') {
          return ''
        }

        const dataToEvaluateNotFound = value === null || value === undefined

        if (dataToEvaluateNotFound) {
          return <span>{t(`messages:riskmatrix.errors.dataToEvaluateNotFound`)}</span>
        }

        function isArrayOfStrings(x) {
          return Array.isArray(x) && x.every(v => typeof v === "string");
        }

        let finalValue = [
          "last_beneficiaries_registration_status_names", 
          "shareholders_ages", 
          "shareholders_business_qty_no_active",
          "shareholders_kyc_status",
          "shareholders_business_with_shareholder_business",
					"shareholders_countries",
					"shareholders_pjs_countries",
          "border_town",
          "shareholder_has_negative_media",
          "last_beneficiaries_pep_history_times",
          "last_beneficiaries_pep_levels",
          "last_beneficiaries_history_sanctions"
        ].includes(row.attribute) && isArrayOfStrings(row.dataResult) ? row.dataResult : value;

        return (
          <RiskMatrixEvaluatedData
            data={finalValue}
            operator={row.operator}
            rawText={row.value}
            attribute={row.attribute}
            dataResult={row.dataResult}
          />
        )
      },
    },
    {
      field: 'operator',
      headerName: t('messages:riskmatrix.title.operator'),
      width: 200,
      renderCell: ({ value, rowNode }) => {
        if (rowNode.type === 'group') {
          return ''
        }

        return (
          !!value && (
            <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {t(`messages:riskmatrix.operator.${value}`)}
            </span>
          )
        )
      },
    },
    {
      field: 'value',
      headerName: t('messages:riskmatrix.title.value'),
      width: 250,
      renderCell: ({ row, rowNode, value }) => {
        if (rowNode.type === 'group') {
          return ''
        }

        return (
          <RiskMatrixValueData
            data={value}
            list={
              ['in_custom_list', 'in_custom_list_composed', 'any_in_custom_list_composed'].includes(row.operator)
                ? row.list
                : undefined
            }
          />
        )
      },
    },
    { field: 'isHit', headerName: t('messages:riskmatrix.title.isHit'), width: 120 },
    { field: 'score', headerName: t('messages:riskmatrix.title.score'), width: 120 },
  ]

  return (
    <Box className="modal">
      <Box className="modal-top">
        <Box display="flex" alignItems="center" gap={5}>
          <RiskMatrixResultTitle riskResult={riskResult} total={typeof riskScore === 'number' ? riskScore : '---'} />

          <Box display="flex" alignItems="center" gap={1}>
            <IconButton
              color="primary"
              onClick={() => handleFetchHistory(riskHistory?.prev)}
              disabled={!riskHistory?.prev}
            >
              <ArrowBackIcon />
            </IconButton>
            <Button disabled={!riskHistory?.next} onClick={() => handleFetchLastHistory()}>
              Last Result
            </Button>
            <IconButton
              color="primary"
              onClick={() => handleFetchHistory(riskHistory?.next)}
              disabled={!riskHistory?.next}
            >
              <ArrowForwardIcon />
            </IconButton>
          </Box>
        </Box>
        <Fab variant="close" onClick={() => setModalOpen(false)}>
          <CloseOutlinedIcon />
        </Fab>
      </Box>
      <Box className={styles.container}>
        {categoryRows && ruleRows && (
          <DataGridPro
            loading={loadingGetLastRiskHistory || loadingGetRiskHistory}
            treeData
            rows={[...categoryRows, ...ruleRows]}
            columns={columns}
            getTreeDataPath={(row) => row.groupedBy.split('/')}
            groupingColDef={{
              headerName: 'Category',
              renderCell: (params) => <CustomGridTreeDataGroupingCell country={country} {...params} />,
              width: 300,
            }}
            disableColumnMenu
            disableColumnSorting
            hideFooter={true}
            rowSelection={false}
            getRowClassName={({ row }) => {
              const classNames = new Set()

              // Check combinator rows for errors
              if (row.combinator && row.isGroup) {
                if (combinatorErrors.has(row.groupedBy)) {
                  classNames.add(styles.rowError)
                }
                return [...classNames].join(' ')
              }

              if (row.isGroup && categoryErrors.includes(row.category)) {
                classNames.add(styles.rowError)
              }
              if (!row.isGroup && (row.dataToEvaluate === null || !row.operatorFound)) {
                classNames.add(styles.rowError)
              }

              if (row.isHit) {
                classNames.add(styles.rowHit)
              }

              return [...classNames].join(' ')
            }}
          />
        )}
      </Box>
    </Box>
  )
}

export default RiskMatrixResultModal
